/**
 * ResearchCompleteness Component
 *
 * Shows a 7-dimension per-category research completeness matrix
 * with a "Fill Research Gaps" action button.
 *
 * Created: 2026-03-02
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  Wrench,
  Microscope,
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';

interface CategoryDimensions {
  name: string;
  path: string;
  dimensions: boolean[];
}

interface ResearchCompletenessData {
  categories: CategoryDimensions[];
  totals: number[];
  totalCategories: number;
  allComplete: boolean;
  dimensionNames: string[];
}

// Short column headers for the matrix table
const SHORT_HEADERS = ['Chars', 'Canon', 'Percep', 'APM', 'PBM', 'Extract', 'AttrInt'];

interface ResearchCompletenessProps {
  siteId: string;
}

export function ResearchCompleteness({ siteId }: ResearchCompletenessProps) {
  const [data, setData] = useState<ResearchCompletenessData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filling, setFilling] = useState(false);
  const [fillMessage, setFillMessage] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [skipIntelligence, setSkipIntelligence] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setError(null);
      const response = await authFetch(`/api/aeo-readiness/site/${siteId}/research-completeness`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load research completeness');
      }

      const result = await response.json();
      setData(result.data);
    } catch (err) {
      console.error('Failed to load research completeness:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    setLoading(true);
    loadData();
  }, [loadData]);

  // Auto-refresh while filling
  useEffect(() => {
    if (!filling) return;

    const interval = setInterval(() => {
      loadData();
    }, 15000);

    return () => clearInterval(interval);
  }, [filling, loadData]);

  // Stop filling when data shows no remaining gaps
  useEffect(() => {
    if (!filling || !data) return;
    const remainingGaps = data.totals.reduce((sum, t) => sum + (data.totalCategories - t), 0);
    if (remainingGaps === 0) {
      setFilling(false);
      setFillMessage('All research gaps filled successfully.');
    }
  }, [data, filling]);

  const handleFillGaps = async () => {
    setShowConfirm(false);
    setFilling(true);
    setFillMessage(null);

    try {
      const response = await authFetch(`/api/aeo-readiness/site/${siteId}/action/fill-research-gaps`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ skipIntelligence }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start fill');
      }

      const result = await response.json();
      setFillMessage(result.message);

      if (result.gaps === 0) {
        setFilling(false);
      }
    } catch (err) {
      console.error('Failed to fill research gaps:', err);
      setFillMessage(err instanceof Error ? err.message : 'Error starting fill');
      setFilling(false);
    }
  };

  if (loading && !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Microscope className="h-5 w-5" />
            Research Completeness
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-1/3" />
            <div className="h-32 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Microscope className="h-5 w-5" />
            Research Completeness
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive">{error}</p>
          <Button size="sm" variant="outline" onClick={() => { setLoading(true); loadData(); }} className="mt-2">
            <RefreshCw className="h-3 w-3 mr-1" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.categories.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Microscope className="h-5 w-5" />
            Research Completeness
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No aeo_wizard categories found. Complete onboarding first.</p>
        </CardContent>
      </Card>
    );
  }

  // Calculate stats
  const completeCategories = data.categories.filter(c => c.dimensions.every(d => d)).length;
  const completionPercent = Math.round((completeCategories / data.totalCategories) * 100);
  const totalGaps = data.totals.reduce((sum, t) => sum + (data.totalCategories - t), 0);

  // Build gap summary for confirm dialog
  const gapSummary = data.dimensionNames
    .map((name, i) => ({ name, missing: data.totalCategories - data.totals[i] }))
    .filter(g => g.missing > 0);

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Microscope className="h-5 w-5" />
                Research Completeness
              </CardTitle>
              <CardDescription>
                7-dimension research status per category
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setLoading(true); loadData(); }}
                disabled={loading}
              >
                <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              {totalGaps > 0 && (
                <Button
                  size="sm"
                  onClick={() => setShowConfirm(true)}
                  disabled={filling}
                >
                  {filling ? (
                    <>
                      <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                      Filling...
                    </>
                  ) : (
                    <>
                      <Wrench className="h-3 w-3 mr-1" />
                      Fill Research Gaps
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Summary bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>
                {data.allComplete ? (
                  <Badge className="bg-green-500">All Complete</Badge>
                ) : (
                  <span className="text-muted-foreground">
                    {completeCategories}/{data.totalCategories} categories fully researched
                  </span>
                )}
              </span>
              <span className="font-mono text-muted-foreground">
                {completionPercent}%
              </span>
            </div>
            <Progress value={completionPercent} className="h-2" />
          </div>

          {/* Fill status message */}
          {fillMessage && (
            <div className="text-sm text-blue-600 bg-blue-50 p-2 rounded">
              {fillMessage}
              {filling && ' — auto-refreshing every 15s'}
            </div>
          )}

          {/* Matrix table */}
          <div className="overflow-x-auto">
            <TooltipProvider>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-background z-10 min-w-[180px]">Category</TableHead>
                    {SHORT_HEADERS.map((header, i) => (
                      <TableHead key={i} className="text-center px-2 min-w-[60px]">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help text-xs">{header}</span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{data.dimensionNames[i]}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.categories.map((cat) => (
                    <TableRow key={cat.name}>
                      <TableCell className="sticky left-0 bg-background z-10 font-medium text-xs truncate max-w-[200px]">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help">{cat.name}</span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{cat.path}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      {cat.dimensions.map((ok, i) => (
                        <TableCell key={i} className="text-center px-2">
                          {ok ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-400 mx-auto" />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {/* Totals row */}
                  <TableRow className="border-t-2 font-medium">
                    <TableCell className="sticky left-0 bg-background z-10">
                      Totals
                    </TableCell>
                    {data.totals.map((total, i) => (
                      <TableCell key={i} className="text-center px-2">
                        <span className={total === data.totalCategories ? 'text-green-600' : 'text-orange-600'}>
                          {total}/{data.totalCategories}
                        </span>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </TooltipProvider>
          </div>

          {/* Gap summary */}
          {gapSummary.length > 0 && (
            <div className="text-xs text-muted-foreground space-y-1">
              <p className="font-medium">Gaps:</p>
              {gapSummary.map(g => (
                <p key={g.name}>{g.name}: {g.missing} categories missing</p>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fill Research Gaps</DialogTitle>
            <DialogDescription>
              This will run AI research to fill {totalGaps} missing dimensions across{' '}
              {data.totalCategories} categories. Progress is logged to the backend console.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {gapSummary.map(g => (
              <div key={g.name} className="flex justify-between text-sm">
                <span>{g.name}</span>
                <Badge variant="secondary">{g.missing} missing</Badge>
              </div>
            ))}
            <div className="flex items-center space-x-2 pt-2 border-t">
              <Checkbox
                id="skipIntelligence"
                checked={skipIntelligence}
                onCheckedChange={(checked) => setSkipIntelligence(checked === true)}
              />
              <label htmlFor="skipIntelligence" className="text-sm cursor-pointer">
                Skip Attr Intelligence (slower step)
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
            <Button onClick={handleFillGaps}>
              <Wrench className="h-4 w-4 mr-2" />
              Fill {totalGaps} Gaps
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
